import React from 'react';
import { shortDayLabel } from '../../utils/appDate';
import { T, NUMERIC, balanceColor, money } from './customers.theme';
import { EditIcon, ListIcon, PhoneIcon, Spinner, StatementIcon, TrashIcon } from './CustomersUI';

/**
 * One customer's book.
 *
 * Same four columns the phone shows — Description, Date, Amount, Balance —
 * oldest first with the running balance reading downward, and the newest line
 * at the bottom. The phone scrolls there on open; here the panel does the same,
 * because the last line is the one that matters.
 *
 * Bixin and Deen Cusub sit in the header rather than a bottom bar: on a screen
 * this size the header is where the eye already is, next to the figure the two
 * buttons change.
 */

// The last track holds the two row actions. They are always drawn, never
// revealed on hover — a control you have to hunt for is one people stop
// believing exists.
const COLUMNS = 'minmax(0, 1fr) 150px 130px 130px 74px';

function RowAction({ label, onClick, tone, children }) {
  const [hovered, setHovered] = React.useState(false);
  const danger = tone === 'danger';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        color: hovered ? (danger ? T.danger : T.ink) : T.inkGhost,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function LedgerRow({ transaction, currency, last, onEdit, onDelete }) {
  const isCharge = Number(transaction.debit || 0) > 0;
  const amount = isCharge ? transaction.debit : transaction.credit;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: 16,
        alignItems: 'center',
        padding: '13px 20px',
        background: last ? T.sunk : 'transparent',
        borderBottom: last ? 'none' : `1px solid ${T.hairline}`,
      }}
    >
      <span
        title={transaction.description}
        style={{
          fontSize: 13,
          color: T.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {transaction.description || '—'}
      </span>

      <span style={{ fontSize: 13, color: T.inkSoft, ...NUMERIC }}>
        {shortDayLabel(transaction.date)}
      </span>

      {/* A charge and a payment are the same number in the same column, so
          colour is the only thing telling them apart. */}
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isCharge ? T.brand : T.paid,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {money(amount, currency)}
      </span>

      <span
        style={{
          fontSize: 13,
          fontWeight: last ? 700 : 400,
          color: last ? T.ink : T.inkSoft,
          textAlign: 'right',
          ...NUMERIC,
        }}
      >
        {money(transaction.balance, currency)}
      </span>

      <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <RowAction label="Edit" onClick={() => onEdit(transaction)}>
          <EditIcon size={14} />
        </RowAction>
        <RowAction label="Delete" tone="danger" onClick={() => onDelete(transaction)}>
          <TrashIcon size={14} />
        </RowAction>
      </span>
    </div>
  );
}

export default function CustomerLedger({
  customer,
  transactions,
  loading,
  currency,
  onPayment,
  onCharge,
  onEdit,
  onStatement,
  onEditTransaction,
  onDeleteTransaction,
}) {
  const bottomRef = React.useRef(null);

  // Jump to the newest line whenever the customer or the ledger changes —
  // the same move the phone makes on open.
  React.useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
  }, [customer && customer._id, transactions.length]);

  if (!customer) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          background: T.canvas,
          textAlign: 'center',
        }}
      >
        <span style={{ display: 'flex', color: T.inkGhost }}>
          <ListIcon />
        </span>
        <span style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.5 }}>
          Select a customer
          <br />
          to see their book
        </span>
      </div>
    );
  }

  const meta = [
    customer.phone,
    customer.district,
    customer.damiinName ? `Damiin: ${customer.damiinName}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        background: T.canvas,
        boxSizing: 'border-box',
      }}
    >
      {/* ── who, what is owed, and the two things you can do about it ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          padding: '22px 28px',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2, color: T.ink }}>
              {customer.name}
            </span>
            <button
              type="button"
              onClick={() => onEdit(customer)}
              title="Update Customer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 30,
                height: 30,
                background: 'transparent',
                border: 'none',
                borderRadius: T.radiusControl,
                color: T.inkFaint,
                cursor: 'pointer',
              }}
            >
              <EditIcon />
            </button>
          </div>
          <span
            style={{
              fontSize: 13.5,
              color: T.inkSoft,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {meta || '—'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: T.inkFaint }}>
              BALANCE
            </span>
            <span
              style={{
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: -0.5,
                color: balanceColor(customer.balance),
                ...NUMERIC,
              }}
            >
              {money(customer.balance, currency)}
            </span>
            {customer.limit ? (
              <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
                Limit {money(customer.limit, currency)}
              </span>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => onPayment(customer)}
              style={{
                width: 128,
                height: 44,
                background: T.shade,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusControl,
                font: 'inherit',
                fontSize: 15,
                fontWeight: 600,
                color: T.brand,
                cursor: 'pointer',
              }}
            >
              Bixin
            </button>
            <button
              type="button"
              onClick={() => onCharge(customer)}
              style={{
                width: 128,
                height: 44,
                background: T.brand,
                border: 'none',
                borderRadius: T.radiusControl,
                font: 'inherit',
                fontSize: 15,
                fontWeight: 600,
                color: T.surface,
                cursor: 'pointer',
              }}
            >
              Deen Cusub
            </button>
          </div>
        </div>
      </div>

      {/* ── the book ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          margin: '20px 28px',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPanel,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLUMNS,
            gap: 16,
            padding: '12px 20px',
            background: T.shade,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, color: T.inkSoft }}>Description</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, color: T.inkSoft }}>Date</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, color: T.inkSoft, textAlign: 'right' }}>Amount</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, color: T.inkSoft, textAlign: 'right' }}>Balance</span>
          <span />
        </div>

        <div ref={bottomRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
                padding: 40,
                color: T.inkFaint,
              }}
            >
              <Spinner size={15} />
              <span style={{ fontSize: 13 }}>Loading…</span>
            </div>
          ) : transactions.length ? (
            transactions.map((transaction, index) => (
              <LedgerRow
                key={transaction._id || index}
                transaction={transaction}
                currency={currency}
                last={index === transactions.length - 1}
                onEdit={onEditTransaction}
                onDelete={onDeleteTransaction}
              />
            ))
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 60,
              }}
            >
              <span style={{ fontSize: 15, color: T.inkFaint }}>Wax xisaab ah ma jiraan</span>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 20px',
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <span style={{ fontSize: 11.5, color: T.inkFaint }}>
            Newest entry is at the bottom; the balance runs downward.
          </span>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => onStatement(customer)}
              disabled={!transactions.length}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 36,
                padding: '0 14px',
                background: T.brand,
                border: 'none',
                borderRadius: T.radiusControl,
                font: 'inherit',
                color: T.surface,
                opacity: transactions.length ? 1 : 0.45,
                cursor: transactions.length ? 'pointer' : 'not-allowed',
              }}
            >
              <StatementIcon />
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>Statement</span>
            </button>

            {/* A tel: link, which is all a desktop can honestly offer. On a
                machine with no handler the click does nothing rather than
                pretending to dial. */}
            <a
              href={customer.phone ? `tel:${customer.phone}` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 36,
                padding: '0 14px',
                background: T.surface,
                border: `1px solid ${T.borderStrong}`,
                borderRadius: T.radiusControl,
                color: customer.phone ? T.inkSoft : T.inkGhost,
                textDecoration: 'none',
                pointerEvents: customer.phone ? 'auto' : 'none',
              }}
            >
              <PhoneIcon />
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>Call</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
